import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Search,
  Check,
  AlertTriangle,
  Mail,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Filter,
  FileSpreadsheet,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '—';
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0,00 €';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(num);
};

const getDefaultDesde = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
};

const getDefaultHasta = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const VencimientoManagement = () => {
  const { token, API_URL } = useAuth();

  // Data
  const [vencimientos, setVencimientos] = useState([]);
  const [totalImporte, setTotalImporte] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [desde, setDesde] = useState(getDefaultDesde);
  const [hasta, setHasta] = useState(getDefaultHasta);
  const [buscarFactura, setBuscarFactura] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  // Sorting
  const [orderBy, setOrderBy] = useState('vencimiento_real');
  const [orderDir, setOrderDir] = useState('ASC');

  // Selection
  const [selectedFacturas, setSelectedFacturas] = useState(new Set());

  // Action loading
  const [actionLoading, setActionLoading] = useState(null);

  const fetchVencimientos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        desde,
        hasta,
        order_by: orderBy,
        order_dir: orderDir,
      });
      if (activeSearch) params.set('buscar_factura', activeSearch);

      const res = await fetch(`${API_URL}/contabilidad/vencimientos?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar vencimientos');
      const data = await res.json();
      setVencimientos(data.vencimientos || []);
      setTotalImporte(data.totalImporte || 0);
      setSelectedFacturas(new Set());
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, desde, hasta, orderBy, orderDir, activeSearch]);

  useEffect(() => {
    fetchVencimientos();
  }, [fetchVencimientos]);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setOrderBy(column);
      setOrderDir('ASC');
    }
  };

  const getSortIcon = (column) => {
    if (orderBy !== column) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-40" />;
    return orderDir === 'ASC'
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-blue-600" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 text-blue-600" />;
  };

  const handleSearch = () => {
    setActiveSearch(buscarFactura.trim());
  };

  const handleClearSearch = () => {
    setBuscarFactura('');
    setActiveSearch('');
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedFacturas(new Set(vencimientos.map((v) => v.factura)));
    } else {
      setSelectedFacturas(new Set());
    }
  };

  const handleSelectFactura = (factura) => {
    setSelectedFacturas((prev) => {
      const next = new Set(prev);
      if (next.has(factura)) {
        next.delete(factura);
      } else {
        next.add(factura);
      }
      return next;
    });
  };

  const handleMarcarPagadas = async () => {
    if (selectedFacturas.size === 0) {
      alert('Debe seleccionar al menos una factura.');
      return;
    }
    if (!window.confirm(`¿Está seguro de que desea marcar ${selectedFacturas.size} factura(s) como pagadas?`)) return;

    try {
      setActionLoading('marcar');
      const res = await fetch(`${API_URL}/contabilidad/vencimientos/marcar-pagadas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ facturas: Array.from(selectedFacturas) }),
      });
      if (!res.ok) throw new Error('Error al marcar como pagadas');
      alert('Facturas marcadas como pagadas correctamente.');
      await fetchVencimientos();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerificarPendientes = async () => {
    if (
      !window.confirm(
        '¿Está seguro de que desea verificar las facturas pendientes de pago? Se enviará un correo de aviso con el listado de facturas vencidas.'
      )
    )
      return;

    try {
      setActionLoading('verificar');
      const res = await fetch(`${API_URL}/contabilidad/vencimientos/verificar-pendientes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Error al verificar pendientes');
      const data = await res.json();
      alert(data.message || `Verificación completada. ${data.count || 0} facturas procesadas.`);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnviarCorreo = async () => {
    if (selectedFacturas.size === 0) {
      alert('Debe seleccionar al menos una factura.');
      return;
    }

    // Check for clients without email
    const sinMail = vencimientos
      .filter((v) => selectedFacturas.has(v.factura) && (!v.mail_admin || v.mail_admin.trim() === ''))
      .map((v) => v.razon_social);

    const uniqueSinMail = [...new Set(sinMail)];

    if (uniqueSinMail.length > 0) {
      if (
        !window.confirm(
          `Los siguientes clientes no tienen mail admin asociado:\n\n${uniqueSinMail.join('\n')}\n\n¿Estás seguro de que quieres continuar?`
        )
      )
        return;
    }

    try {
      setActionLoading('correo');
      const res = await fetch(`${API_URL}/contabilidad/vencimientos/enviar-correo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          facturas: Array.from(selectedFacturas),
          fecha1: desde,
          fecha2: hasta,
        }),
      });
      if (!res.ok) throw new Error('Error al enviar correos');
      alert('Correos enviados correctamente.');
      await fetchVencimientos();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleExcelDownload = async () => {
    try {
      setActionLoading('excel');
      const params = new URLSearchParams({ desde, hasta, tipo: 'vencimientos' });
      const res = await fetch(`${API_URL}/contabilidad/vencimientos/excel?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al descargar Excel');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturas_vencimientos_${desde}_${hasta}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const allSelected = vencimientos.length > 0 && selectedFacturas.size === vencimientos.length;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Consulta de Vencimientos</h1>
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Desde</label>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Hasta</label>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={fetchVencimientos}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Actualizar
            </button>
            <button
              onClick={handleExcelDownload}
              disabled={actionLoading !== null}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Descargar Excel
            </button>
          </div>
        </div>

        {/* Search row */}
        <div className="flex items-center gap-2 mt-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por Nº Factura"
              value={buscarFactura}
              onChange={(e) => setBuscarFactura(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            Buscar
          </button>
          {activeSearch && (
            <button
              onClick={handleClearSearch}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Summary bar */}
        {!loading && vencimientos.length > 0 && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">
              Vencimientos del <span className="font-medium">{formatDate(desde)}</span> al <span className="font-medium">{formatDate(hasta)}</span>
              {activeSearch && (
                <span className="ml-2 text-gray-400">— Filtrado por: {activeSearch}</span>
              )}
            </p>
            <p className="text-sm font-semibold text-gray-700">
              Total: {formatCurrency(totalImporte)}
            </p>
          </div>
        )}

        {/* Table card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <span className="ml-3 text-gray-500">Cargando vencimientos...</span>
            </div>
          ) : vencimientos.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No se encontraron vencimientos en el rango seleccionado.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CIF</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                      onClick={() => handleSort('cliente')}
                    >
                      <span className="flex items-center">
                        Razón Social
                        {getSortIcon('cliente')}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vto. Factura</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                      onClick={() => handleSort('vencimiento_real')}
                    >
                      <span className="flex items-center">
                        Vto. Real
                        {getSortIcon('vencimiento_real')}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días Pago</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº Factura</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forma Pago</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Últ. Correo</th>
                    <th className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vencimientos.map((v, idx) => (
                    <tr
                      key={`${v.factura}-${idx}`}
                      className="hover:bg-blue-50/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">{v.cif}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 truncate" title={v.razon_social}>
                        {v.razon_social}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(v.fecha_vencimiento_factura)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-blue-700">{formatDate(v.vencimiento_real)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {v.dias_pago ? v.dias_pago : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">{v.factura}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(v.total)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate" title={v.forma_pago_desc}>
                        {v.forma_pago_desc || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(v.ultimo_correo_enviado)}</td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedFacturas.has(v.factura)}
                          onChange={() => handleSelectFactura(v.factura)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-300">
                    <td colSpan={6} className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">
                      Total Importe:
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700 text-sm">
                      {formatCurrency(totalImporte)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {!loading && vencimientos.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            <button
              onClick={handleMarcarPagadas}
              disabled={actionLoading !== null}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading === 'marcar' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Marcar como Pagadas las Facturas Seleccionadas
                </>
              )}
            </button>

            <button
              onClick={handleVerificarPendientes}
              disabled={actionLoading !== null}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading === 'verificar' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4" />
                  Verificar Pendientes de Pago
                </>
              )}
            </button>

            <button
              onClick={handleEnviarCorreo}
              disabled={actionLoading !== null}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading === 'correo' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Enviar Correo a Facturas Pendientes
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VencimientoManagement;
